import VModalFunc from '@/components/vkyc/v-modal-function';
import { VAction, VendorObjectType } from '@/interfaces/event-log';
import i18n from 'i18n';
import { observable } from 'mobx';
import { OTPublisherRef, OTSubscriberRef } from 'opentok-react';
import { Error, PublisherStats, SubscriberStats } from 'opentok-react/types/opentok';
import React from 'react';
import socketStore from 'store/socket';
import statsStore from 'store/statistics';
import { eventTrack } from './event-track';
import webSocket from './socket';

const OT = require('@opentok/client');

process.env.MODE !== 'production' && process.env.MODE !== 'staging'
  ? OT.setLogLevel(OT.DEBUG)
  : OT.setLogLevel(OT.LOG);

// test if the browser supports WebRTC
// console.info('------- begin -------');
// const result = OT.checkSystemRequirements();
// if (result === 1) {
//   console.info('support WebRTC');
// } else {
//   console.info('does not support WebRTC', result);
//   // The client does not support WebRTC.
//   // You can display your own message.
// }
// console.info(OT.checkSystemRequirements());
// console.info('------- end -------');

class Vendor {
  public otPublisherRef = React.createRef<OTPublisherRef>();
  public otSubscriberRef = React.createRef<OTSubscriberRef>();

  @observable
  public subVidEnabled = false;

  @observable
  public pubVidEnabled = false;

  public sessionEventHandlers = {
    sessionConnected: () => {
      statsStore.updateConnection('Connected');
    },
    sessionDisconnected: () => {
      statsStore.updateConnection('Disconnected');
    },
    sessionReconnected: () => {
      statsStore.updateConnection('Reconnected');
    },
    sessionReconnecting: () => {
      statsStore.updateConnection('Reconnecting');
    },
    archiveStarted: (...rest: any) => {
      this.vendorLog('archiveStarted', rest);
    },
    archiveStopped: (...rest: any) => {
      this.vendorLog('archiveStopped', rest);
    },
    connectionCreated: (...rest: any) => {
      this.vendorLog('connectionCreated', rest);
    },
    connectionDestroyed: (...rest: any) => {
      this.vendorLog('connectionDestroyed', rest);
    },
    signal: () => (...rest: any) => {
      this.vendorLog('signal', rest);
    },
    streamCreated: (...rest: any) => {
      this.vendorLog('streamCreated', rest);
    },
    streamDestroyed: (...rest: any) => {
      this.vendorLog('streamDestroyed', rest);
    },
    streamPropertyChanged: (...rest: any) => {
      this.vendorLog('streamPropertyChanged', rest[0].changedProperty, rest[0].newValue);
      if (rest[0].changedProperty === 'hasVideo' && !rest[0].newValue) {
        console.log('video changed to blocked');
        this.pubVidEnabled = false;
        VModalFunc({
          type: 'error',
          title: 'ERROR',
          content: i18n.t('opentok.message.mediaAccessPublisher'),
          details: `video is disabled`,
          destroyOthers: true,
        });
      }
    },
  };

  public publisherEventHandlers = {
    accessDenied: () => {
      statsStore.updateTip('User denied access to media source');
      // socketStore.setStatusToSorry();
      // socketStore.goToComplete();
      // VModalFunc({
      //   type: 'error',
      //   title: 'ERROR',
      //   content: i18n.t('opentok.message.mediaAccessPublisher'),
      //   destroyOthers: true,
      // });
    },
    streamCreated: () => {
      statsStore.updateTip('Publisher stream created');
    },
    streamDestroyed: (result: { reason: string }) => {
      statsStore.updateTip(`Publisher stream destroyed because: ${result.reason}`);
    },
    accessAllowed: () => {
      this.vendorLog('Publisher allowed access');
    },
    accessDialogOpened: () => {
      this.vendorLog('access dialog opened');
      // start count down
      socketStore.updateMediaAccessDialogClosed(false);
      socketStore.initMediaAccessCountdownTimer();
    },
    accessDialogClosed: () => {
      this.vendorLog('access dialog closed');
      socketStore.updateMediaAccessDialogClosed(true);
      socketStore.resetMediaAccessCountdown();
    },
    destroyed: () => {
      this.vendorLog('Publisher video element destroyed');
    },
    mediaStopped: (e: any) => {
      this.vendorLog('Publisher video stopped');
      this.pubVidEnabled = false;
      VModalFunc({
        type: 'error',
        title: 'ERROR',
        content: i18n.t('opentok.message.mediaAccessPublisher'),
        details: `${e.track.kind} is disabled`,
        destroyOthers: true,
      });
    },
    videoElementCreated: () => {
      this.vendorLog('Publisher video enabled');
      this.pubVidEnabled = true;
    },
  };

  public subscriberEventHandlers = {
    videoEnabled: () => {
      this.vendorLog('Subscriber video enabled');
      this.subVidEnabled = true;
      // Modal.destroyAll();
    },
    videoDisabled: () => {
      this.vendorLog('Subscriber video disabled');
      this.subVidEnabled = false;
      VModalFunc({
        type: 'error',
        title: 'ERROR',
        content: i18n.t('opentok.message.mediaAccessSubscriber'),
        destroyOthers: true,
      });
    },
    audioBlocked: () => {
      this.vendorLog('Subscriber audioBlocked');
    },
    audioLevelUpdated: () => {
      // this.vendorLog('Subscriber audioLevelUpdated');
    },
    audioUnblocked: () => {
      this.vendorLog('Subscriber audioUnblocked');
    },
    connected: () => {
      this.vendorLog('Subscriber connected');
    },
    destroyed: () => {
      this.vendorLog('Subscriber destroyed');
    },
    disconnected: () => {
      this.vendorLog('Subscriber disconnected');
    },
    videoDimensionsChanged: () => {
      this.vendorLog('Subscriber videoDimensionsChanged');
    },
    videoDisableWarning: () => {
      this.vendorLog('Subscriber videoDisableWarning');
    },
    videoDisableWarningLifted: () => {
      this.vendorLog('Subscriber videoDisableWarningLifted');
    },
    videoElementCreated: () => {
      this.vendorLog('Subscriber videoElementCreated');
      this.subVidEnabled = true;
    },
  };

  private publisher: any;
  private subscriber: any;

  public vendorLog = (...rest: any) => {
    OT.log(...rest);
  };

  public vendorError = (...rest: any) => {
    OT.error(...rest);
  };

  public getPublisher = () => {
    this.updatePublisher();
    return this.publisher;
  };

  public getSubscriber = () => {
    this.updateSubscriber();
    return this.subscriber;
  };

  public updateSubscriber = () => {
    if (this.otSubscriberRef?.current) {
      this.subscriber = this.otSubscriberRef.current.getSubscriber();
    } else {
      this.vendorLog('no subscriber');
    }
  };

  public updatePublisher = () => {
    if (this.otPublisherRef?.current) {
      this.publisher = this.otPublisherRef.current.getPublisher();
    } else {
      this.vendorLog('no publisher');
    }
  };

  public getStatsSubscriber = () => {
    try {
      this.getSubscriber().getStats((error: Error | undefined, data: SubscriberStats) => {
        if (error) {
          this.vendorError('subscriber stats get error', error);
        } else {
          statsStore.updateSubscriberInfo(data);
        }
      });
    } catch (e) {
      this.vendorError('no data, subscriber', e);
    }
  };

  public getStatsPublisher = () => {
    try {
      this.getPublisher().getStats((error: Error | undefined, array: PublisherStats[]) => {
        if (error) {
          this.vendorError('publisher stats get error', error);
        } else {
          statsStore.updatePublisherInfo(array[0]?.stats);
        }
      });
    } catch (e) {
      this.vendorError('no data, publisher', e);
    }
  };

  public onSessionError = (error: Error) => {
    statsStore.updateTip(`session error: ${error}`);
    this.pushErrorEvent(VendorObjectType.SESSION, error);
    webSocket.replyAssignAgentIfSuccess(false);
    webSocket.clientSendVendorErrorLeave(error);
  };

  public onPublish = () => {
    eventTrack.pushEvent({
      action: VAction.OPENTOK_PUBLISHED,
    });
    statsStore.updateTip('publish successfully');
    webSocket.replyAssignAgentIfSuccess(true);
  };

  public onPublishError = (error: Error) => {
    statsStore.updateTip(`publish error: ${error}`);
    this.pushErrorEvent(VendorObjectType.PUBLISHER, error);
    webSocket.replyAssignAgentIfSuccess(false);
    webSocket.clientSendVendorErrorLeave(error);
  };

  public onSubscribe = () => {
    eventTrack.pushEvent({
      action: VAction.OPENTOK_SUBSCRIBED,
    });
    statsStore.updateTip('Subscribe Successfully');
    this.getStatsSubscriber();
    this.getStatsPublisher();
    // statsStore.initPublisherTimer();
    // statsStore.initSubscriberTimer();
    statsStore.initGetStatsTimer();
  };

  public onSubscribeError = (error: Error) => {
    this.pushErrorEvent(VendorObjectType.SUBSCRIBER, error);
    statsStore.updateTip(`suscribe error: ${error}`);
  };

  public onConnect = () => {
    eventTrack.pushEvent({
      action: VAction.OPENTOK_CONNECTED,
    });
    this.vendorLog('--- session onConnect ---');
  };

  private pushErrorEvent = (type: VendorObjectType, error: Error) => {
    eventTrack.pushEvent({
      action: VAction.OPENTOK_FAILED,
      extra: {
        sessionId: socketStore.getSocketData().videoCallInfo?.sessionId,
        type: type,
        details: error,
      },
    });
  };
}

const vendor = new Vendor();
export default vendor;
